// apps/fe/src/modules/catalog/screens/catalog-search-screen.tsx

'use client';
import React, { useEffect, useMemo, useCallback } from 'react';
import { EventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { useSearch } from '../hooks/use-search';
import { CatalogSearchInitialData } from '../types/catalog-search-data';
import BaseCatalogPage from '../ui/pages/base-catalog-page';
import { useProfileCatalogData } from '../hooks/use-profile-catalog-data';
import { useAuthStore } from '@/auth';
import logger from 'src/shared/utils/logger';
import { CatalogFilters } from '../types/filter';

interface CatalogSearchScreenProps {
  dict: CatalogDict;
  eventBus: EventBus<CatalogEvents>;
  initialData: CatalogSearchInitialData;
  query: string;
  isLoggedIn: boolean;
}

/**
 * Search results screen for the catalog.
 * Shows courses filtered by search query.
 *
 * Wrapped in React.memo to prevent unnecessary re-renders
 */
const CatalogSearchScreen = React.memo(function CatalogSearchScreen({
  dict,
  eventBus,
  initialData,
  query,
  isLoggedIn,
}: CatalogSearchScreenProps) {
  const { user } = useAuthStore();

  // Memoize the catalog params to avoid new references on each render
  const catalogParams = useMemo(
    () => ({
      query,
      courseTypes: ['Masterclass', 'Serie', 'Percorsi'],
    }),
    [query],
  );

  // Memoize filter data from SSG to always be available immediately
  const filtersData = useMemo(() => {
    // Ensure the filters object has all the required properties
    const hasValidFilters =
      initialData.filters &&
      initialData.filters.instruments &&
      initialData.filters.sidebar &&
      initialData.filters.sidebar.skillLevels &&
      initialData.filters.sidebar.genres &&
      initialData.filters.sidebar.topics &&
      initialData.filters.sidebar.type;

    if (!hasValidFilters) {
      logger.warn('Search data filters are missing or malformed, using default structure.');

      // Default filter structure if missing
      const defaultFilters: CatalogFilters = {
        instruments: [],
        sidebar: {
          skillLevels: [],
          genres: [],
          topics: [],
          type: [],
        },
      };

      return defaultFilters;
    }

    return initialData.filters;
  }, [initialData.filters]);

  // Rehydrate search data with user-specific information if logged in
  const {
    data: enhancedData,
    loading: loadingUserData,
    error: userDataError,
    hasUserData,
    updateFavoriteStatus,
    setActiveFilters,
    preservedFilters,
  } = useProfileCatalogData({
    dataType: 'search',
    initialData,
    catalogParams,
    eventBus,
  });

  // Memoize search data to avoid re-rendering
  const searchData = useMemo(() => {
    // If there was an error or user data is not available yet, use the initial SSG data
    const contentData =
      userDataError || !hasUserData
        ? initialData
        : user && hasUserData
          ? enhancedData
          : initialData;

    // Important: Always ensure we have filters even during loading
    return {
      ...contentData,
      filters: filtersData, // Always use filters from SSG
    };
  }, [user, hasUserData, enhancedData, initialData, userDataError, filtersData]);

  // Set up search-specific filters with proper authentication flag and state coordination
  const search = useSearch({
    eventBus,
    filters: filtersData, // Always use SSG filters
    query,
    withAuthentication: !!user,
    isEnrichingData: loadingUserData,
    onFilterChange: setActiveFilters,
    initialActiveFilters: preservedFilters,
  });

  // Handle favorite changes from filtered view - memoized to maintain reference equality
  const handleFavoriteChange = useCallback(
    (courseId: string, favouriteId: string | undefined) => {
      // Update the enriched data to maintain consistency
      if (updateFavoriteStatus) {
        console.log('Updating favorite in search data:', courseId, favouriteId);
        updateFavoriteStatus(courseId, favouriteId);
      }
    },
    [updateFavoriteStatus],
  );

  // Emit the view event to signal the search view is ready - once on mount
  useEffect(() => {
    eventBus.emit(CatalogEventKeys.search.view, undefined);
  }, [eventBus]);

  // Function to determine if we're in a stale loading state and force clear it
  const checkAndResetLoadingState = useCallback(() => {
    // If we've been loading for more than 5 seconds and have data, force loading to false
    if (search.loadingSearchCatalogData && search.filteredSearchItems.length > 0) {
      setTimeout(() => {
        if (search.loadingSearchCatalogData && search.filteredSearchItems.length > 0) {
          console.warn('Forcing loading state to false after timeout');
          // Here we'd need a direct way to update loading state
          // This is just a safeguard mechanism
        }
      }, 5000);
    }
  }, [search.loadingSearchCatalogData, search.filteredSearchItems.length]);

  // Apply the safety check for loading state
  useEffect(() => {
    checkAndResetLoadingState();
  }, [checkAndResetLoadingState]);

  return (
    <BaseCatalogPage
      dict={dict}
      eventBus={eventBus}
      isLoggedIn={isLoggedIn}
      filteredItems={search.filteredSearchItems}
      loadingCatalogData={search.loadingSearchCatalogData || loadingUserData}
      filteringCatalogData={true}
      activeFilters={search.searchFilters}
      parentInstruments={search.parentSearchInstruments}
      filters={filtersData}
      pageTitle={`Risultati per ${decodeURI(query)}`}
      showBackButton={true}
      lastCourseElementRef={search.lastCourseElementRef}
      instrumentsFilterOnChange={search.instrumentsSearchFilterOnChange}
      handleSkillLevelsChange={search.handleSkillLevelsChange}
      handleGenresChange={search.handleGenresChange}
      handleTopicsChange={search.handleTopicsChange}
      handleTypeChange={search.handleTypeChange}
      onFavoriteChange={handleFavoriteChange}
      isEnrichingData={loadingUserData}
      pageType="search"
      onResetSidebarFilters={search.onResetSidebarFilters}
      isAnyFilterActive={search.isAnyFilterActive}
    />
  );
});

export default CatalogSearchScreen;
